import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export {};

const BASE_URL = "/api";

type IndustryKey = "美容医療" | "エステティック" | "異業種" | "その他" | "BtoB" | "";

type SalesMethodData = {
  merchant_id: string;
  marketing_method: string;
  customer_segment: string;
  male_percent: string;
  female_percent: string;
  customer_ratio_employee: string;
  customer_ratio_housewife: string;
  customer_ratio_student: string;
  age_min: string;
  age_max: string;
  cancellation_count: string;
  medical_liability_insurance: string;
  b2b_marketing_method: string;
  b2b_customer_segment: string;
  b2b_customer_ratio_corporate: string;
  b2b_customer_ratio_sole: string;
  is_completed: boolean;
  updated_at?: string;
};

const getStaffAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("staff_google_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
        alignItems: "center",
      }}
    >
      <div style={{ fontWeight: 800, color: "#0f172a" }}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          outline: "none",
          fontWeight: 600,
          color: "#0f172a",
          background: "#fff",
        }}
      />
    </div>
  );
}

function ReadonlyInputBox({
  label,
  value,
  required,
  suffix,
}: {
  label: string;
  value: string | undefined | null;
  required?: boolean;
  suffix?: string;
}) {
  const displayValue = value && String(value).trim() ? String(value) : "—";

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>

      <div
        style={{
          position: "relative",
          width: "100%",
          height: 44,
          padding: suffix ? "0 70px 0 12px" : "0 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#f8fafc",
          color: displayValue === "—" ? "#94a3b8" : "#0f172a",
          fontSize: 14,
          fontWeight: 700,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {displayValue}

        {suffix && (
          <span
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#475569",
              fontSize: 14,
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function SectionBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: 12,
        background: "#ffffff",
      }}
    >
      <div style={{ fontWeight: 1000, marginBottom: 10, color: "#0f172a" }}>{title}</div>
      {children}
    </div>
  );
}

function ThreeCol({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)",
        gap: 20,
        alignItems: "start",
      }}
    >
      {children}
    </div>
  );
}

export default function StaffFormEditSalesMethod() {
  const navigate = useNavigate();
  const { merchantId } = useParams();

  const contentRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [data, setData] = useState<SalesMethodData | null>(null);
  const [draft, setDraft] = useState<SalesMethodData | null>(null);

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [industry, setIndustry] = useState<IndustryKey>("");

  const isBtoB = industry === "BtoB";
  const isBeautyMedical = industry === "美容医療";

  const load = async () => {
    if (!merchantId) return;

    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      const progressRes = await fetch(`${BASE_URL}/staff/progress/${merchantId}`, {
        headers: getStaffAuthHeaders(),
      });
      const progressJson = await progressRes.json().catch(() => ({}));

      if (progressRes.ok) {
        const rawIndustry = String(progressJson?.merchant?.industry || "").trim();
        setIndustry(rawIndustry as IndustryKey);
      }

      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form5_sales_method`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "fetch failed");

      const d = json?.data || null;

      const loaded: SalesMethodData = {
        merchant_id: d?.merchant_id || merchantId,
        marketing_method: d?.marketing_method || "",
        customer_segment: d?.customer_segment || "",
        male_percent: d?.male_percent || "",
        female_percent: d?.female_percent || "",
        customer_ratio_employee: d?.customer_ratio_employee || "",
        customer_ratio_housewife: d?.customer_ratio_housewife || "",
        customer_ratio_student: d?.customer_ratio_student || "",
        age_min: d?.age_min || "",
        age_max: d?.age_max || "",
        cancellation_count: d?.cancellation_count || "",
        medical_liability_insurance: d?.medical_liability_insurance || "",
        b2b_marketing_method: d?.b2b_marketing_method || "",
        b2b_customer_segment: d?.b2b_customer_segment || "",
        b2b_customer_ratio_corporate: d?.b2b_customer_ratio_corporate || "",
        b2b_customer_ratio_sole: d?.b2b_customer_ratio_sole || "",
        is_completed: !!d?.is_completed,
        updated_at: d?.updated_at || "",
      };

      setData(loaded);
      setDraft(loaded);
      setLoading(false);
    } catch (e: any) {
      setError(e?.message || "フォーム⑤（販売方法）の取得に失敗しました。");
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const save = async () => {
    if (!merchantId || !draft) return;

    setSaving(true);
    setError("");
    setSuccessMsg("");

    try {
      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form5_sales_method`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          marketing_method: draft.marketing_method,
          customer_segment: draft.customer_segment,
          male_percent: draft.male_percent,
          female_percent: draft.female_percent,
          customer_ratio_employee: draft.customer_ratio_employee,
          customer_ratio_housewife: draft.customer_ratio_housewife,
          customer_ratio_student: draft.customer_ratio_student,
          age_min: draft.age_min,
          age_max: draft.age_max,
          cancellation_count: draft.cancellation_count,
          medical_liability_insurance: draft.medical_liability_insurance,
          b2b_marketing_method: draft.b2b_marketing_method,
          b2b_customer_segment: draft.b2b_customer_segment,
          b2b_customer_ratio_corporate: draft.b2b_customer_ratio_corporate,
          b2b_customer_ratio_sole: draft.b2b_customer_ratio_sole,
        }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "save failed");

      setSuccessMsg("保存しました。");
      setEditMode(false);
      await load();
    } catch (e: any) {
      setError(e?.message || "保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    if (!merchantId) return;

    try {
      setDownloading(true);

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        position -= pdfHeight;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`フォーム5_販売方法_${merchantId}.pdf`);
    } catch (e) {
      console.error(e);
      alert("PDFの作成に失敗しました。もう一度お試しください。");
    } finally {
      setDownloading(false);
    }
  };

  const toggleEditMode = () => {
    if (!data) return;

    if (editMode) {
      setDraft({ ...data });
      setSuccessMsg("");
      setError("");
      setEditMode(false);
      return;
    }

    setDraft({ ...data });
    setSuccessMsg("");
    setError("");
    setEditMode(true);
  };

  return (
    <StaffLayout title="フォーム⑤ 販売方法（確認・編集）" active="progress">
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => navigate(`/staff/progress/${merchantId}`)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#0f172a",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          進捗詳細へ戻る
        </button>

        <button
          onClick={downloadPdf}
          disabled={downloading || loading || !!error}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: downloading ? "#1e293b" : "#0b1f3b",
            color: "#fff",
            fontWeight: 900,
            cursor: downloading ? "not-allowed" : "pointer",
            opacity: downloading || loading || !!error ? 0.8 : 1,
          }}
        >
          {downloading ? "PDF作成中..." : "PDFダウンロード"}
        </button>

        <button
          onClick={toggleEditMode}
          disabled={loading || !!error || !data}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: editMode ? "#fff" : "#0b1f3b",
            color: editMode ? "#0b1f3b" : "#fff",
            fontWeight: 900,
            cursor: "pointer",
            opacity: loading || !!error || !data ? 0.8 : 1,
          }}
        >
          {editMode ? "編集をやめる" : "編集する（スタッフ）"}
        </button>

        {editMode && (
          <button
            onClick={save}
            disabled={saving || loading || !!error || !draft}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #16a34a",
              background: saving ? "#14532d" : "#16a34a",
              color: "#fff",
              fontWeight: 900,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving || loading || !!error || !draft ? 0.8 : 1,
            }}
          >
            {saving ? "保存中..." : "保存"}
          </button>
        )}
      </div>

      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginTop: 10,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {successMsg && (
        <div
          style={{
            marginTop: 10,
            background: "#ecfdf5",
            border: "1px solid #bbf7d0",
            color: "#166534",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {successMsg}
        </div>
      )}

      {!loading && !error && data && (
        <div ref={contentRef} style={{ display: "grid", gap: 12, background: "#ffffff" }}>
          <div
            style={{
              border: "1px solid #bae6fd",
              background: "#f0f9ff",
              borderRadius: 14,
              padding: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>フォーム状態</div>
            <div style={{ fontWeight: 900, color: data.is_completed ? "#166534" : "#9a3412" }}>
              {data.is_completed ? "完了（保存済み）" : "未完了（または未保存）"}
            </div>
            <div style={{ marginTop: 6, color: "#475569", fontWeight: 800, fontSize: 13 }}>
              加盟店ID：{merchantId}
              {data.updated_at ? `　｜　最終更新：${data.updated_at}` : ""}
            </div>
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              background: "#fff",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 12,
                fontWeight: 900,
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              入力内容（{editMode ? "編集モード（スタッフ）" : "確認"}）
            </div>

            {!editMode && (
              <div style={{ padding: 12 }}>
                <div style={{ marginBottom: 12, display: "flex", gap: 10, alignItems: "center" }}>
                  <div style={{ fontWeight: 900, color: "#0f172a" }}>業種：</div>
                  <div
                    style={{
                      background: "#0b1f3b",
                      color: "#fff",
                      fontWeight: 900,
                      padding: "6px 12px",
                      borderRadius: 999,
                      fontSize: 12,
                    }}
                  >
                    {(industry || "未設定").trim() || "未設定"}
                  </div>
                </div>

                {!isBtoB && (
                  <div
                    style={{
                      border: "1px solid #cbd5e1",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 12px",
                        fontWeight: 900,
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      販売方法
                    </div>

                    <div style={{ padding: 12, display: "grid", gap: 12 }}>
                      <SectionBox title="集客方法">
                        <ReadonlyInputBox label="集客方法" value={data.marketing_method} />
                      </SectionBox>

                      <SectionBox title="男女比">
                        <ThreeCol>
                          <ReadonlyInputBox label="男性" value={data.male_percent} suffix="%" />
                          <ReadonlyInputBox label="女性" value={data.female_percent} suffix="%" />
                          <div />
                        </ThreeCol>
                      </SectionBox>

                      <SectionBox title="顧客構成比">
                        <ThreeCol>
                          <ReadonlyInputBox label="会社員" value={data.customer_ratio_employee} suffix="%" />
                          <ReadonlyInputBox label="主婦" value={data.customer_ratio_housewife} suffix="%" />
                          <ReadonlyInputBox label="学生" value={data.customer_ratio_student} suffix="%" />
                        </ThreeCol>
                      </SectionBox>

                      <SectionBox title="年齢層">
                        <ThreeCol>
                          <ReadonlyInputBox label="最小" value={data.age_min} suffix="歳" />
                          <ReadonlyInputBox label="最大" value={data.age_max} suffix="歳" />
                          <div />
                        </ThreeCol>
                      </SectionBox>

                      <SectionBox title="中途解約件数（月間平均）">
                        <ThreeCol>
                          <ReadonlyInputBox label="件数" value={data.cancellation_count} suffix="件" />
                          <div />
                          <div />
                        </ThreeCol>
                      </SectionBox>

                      {isBeautyMedical && (
                        <SectionBox title="美容医療賠償責任制度の加入の有無">
                          <ThreeCol>
                            <ReadonlyInputBox
                              label="加入の有無"
                              value={data.medical_liability_insurance}
                              required
                            />
                            <div />
                            <div />
                          </ThreeCol>
                        </SectionBox>
                      )}
                    </div>
                  </div>
                )}

                {isBtoB && (
                  <div
                    style={{
                      border: "1px solid #cbd5e1",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px 12px",
                        fontWeight: 900,
                        background: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      BtoB
                    </div>

                    <div style={{ padding: 12, display: "grid", gap: 12 }}>
                      <SectionBox title="集客方法">
                        <ThreeCol>
                          <ReadonlyInputBox label="集客方法" value={data.b2b_marketing_method} />
                          <ReadonlyInputBox label="顧客層" value={data.b2b_customer_segment} />
                          <div />
                        </ThreeCol>
                      </SectionBox>

                      <SectionBox title="顧客構成比">
                        <ThreeCol>
                          <ReadonlyInputBox label="法人" value={data.b2b_customer_ratio_corporate} suffix="%" />
                          <ReadonlyInputBox label="個人事業主" value={data.b2b_customer_ratio_sole} suffix="%" />
                          <div />
                        </ThreeCol>
                      </SectionBox>
                    </div>
                  </div>
                )}
              </div>
            )}

            {editMode && draft && (
              <>
                <Field label="集客方法" value={draft.marketing_method} onChange={(v) => setDraft({ ...draft, marketing_method: v })} />
                <Field label="顧客層" value={draft.customer_segment} onChange={(v) => setDraft({ ...draft, customer_segment: v })} />
                <Field label="男性割合（%）" value={draft.male_percent} onChange={(v) => setDraft({ ...draft, male_percent: v })} />
                <Field label="女性割合（%）" value={draft.female_percent} onChange={(v) => setDraft({ ...draft, female_percent: v })} />
                <Field label="顧客割合（会社員）" value={draft.customer_ratio_employee} onChange={(v) => setDraft({ ...draft, customer_ratio_employee: v })} />
                <Field label="顧客割合（主婦）" value={draft.customer_ratio_housewife} onChange={(v) => setDraft({ ...draft, customer_ratio_housewife: v })} />
                <Field label="顧客割合（学生）" value={draft.customer_ratio_student} onChange={(v) => setDraft({ ...draft, customer_ratio_student: v })} />
                <Field label="年齢（最小）" value={draft.age_min} onChange={(v) => setDraft({ ...draft, age_min: v })} />
                <Field label="年齢（最大）" value={draft.age_max} onChange={(v) => setDraft({ ...draft, age_max: v })} />
                <Field label="解約件数" value={draft.cancellation_count} onChange={(v) => setDraft({ ...draft, cancellation_count: v })} />
                <Field
                  label="医療賠償責任保険（必須）"
                  value={draft.medical_liability_insurance}
                  onChange={(v) => setDraft({ ...draft, medical_liability_insurance: v })}
                  placeholder="例：加入 / 未加入"
                />
                <Field label="BtoB 集客方法" value={draft.b2b_marketing_method} onChange={(v) => setDraft({ ...draft, b2b_marketing_method: v })} />
                <Field label="BtoB 顧客層" value={draft.b2b_customer_segment} onChange={(v) => setDraft({ ...draft, b2b_customer_segment: v })} />
                <Field label="BtoB 顧客割合（法人）" value={draft.b2b_customer_ratio_corporate} onChange={(v) => setDraft({ ...draft, b2b_customer_ratio_corporate: v })} />
                <Field label="BtoB 顧客割合（個人事業主）" value={draft.b2b_customer_ratio_sole} onChange={(v) => setDraft({ ...draft, b2b_customer_ratio_sole: v })} />
              </>
            )}
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
